import React from 'react';
import {
  Font,
  Page,
  StyleSheet,
  Text,
  View,
  Link,
} from '@react-pdf/renderer';

import _ from 'lodash';

import { LogoViewType } from './types';
import formatImageSource from '../utils/formatImageSource';
import getLogoDimensionsForView, { RectangularDimensions } from '../utils/getLogoDimensionsForView';
import radiusToText from '../utils/radiusToText';
import removeHyphenation from '../utils/removeHyphenation';

import ZipCodeSvg from '../../../molecules/ZipCodeSvg';
import QrCodeSvg from '../../../molecules/QrCodeSvg';

import PdfImageFromAzureWithOptions from '../../../atoms/PdfImageFromAzureWithOptions';
import PdfImageFromAzureWithUri from '../../../atoms/PdfImageFromAzureWithUri';

removeHyphenation(Font);

const Flyer = ({
  isCoBranded,
  coBrand,
  primaryBrand,
  lbBrand,
  themeData,
  imgSrc,
  radius,
  zipCode,
  uuid,
}): JSX.Element => {
  const primaryLogoView: LogoViewType = {
    width: 219,
    height: 60,
    position: 'absolute',
    left: 414,
    top: 36,
  };
  const primaryLogoDimensions: RectangularDimensions = getLogoDimensionsForView(
    {
      width: primaryBrand.logoWidth,
      height: primaryBrand.logoHeight,
    },
    {
      width: primaryLogoView.width,
      height: primaryLogoView.height,
    },
  );
  const coBrandLogoView: LogoViewType = {
    width: 96,
    height: 60,
    position: 'absolute',
    left: 414,
    bottom: 36,
  };
  const coBrandLogoDimensions: RectangularDimensions = getLogoDimensionsForView(
    {
      width: coBrand.logoWidth,
      height: coBrand.logoHeight,
    }, {
      width: coBrandLogoView.width,
      height: coBrandLogoView.height,
    }
  );
  const lbLogoView: LogoViewType = {
    width: 96,
    height: 60,
    position: 'absolute',
    left: 537,
    bottom: 36,
  };
  const lbLogoDimensions: RectangularDimensions = getLogoDimensionsForView(
    {
      width: 500,
      height: 125,
    }, {
      width: lbLogoView.width,
      height: lbLogoView.height,
    }
  );

  // NOTE: Can be shared between Flyers
  const Styles = StyleSheet.create({
    announcement: {
      fontFamily: 'Courier',
      textTransform: 'uppercase',
      fontSize: '12pt',
    },
    announcementTheme: {
      fontFamily: 'Times-Roman',
      fontSize: '30pt',
      lineHeight: 1.17,
    },
    announcementView: {
      width: '342px',
      height: '60px',
      position: 'absolute',
      top: '36.5px',
      left: '36.5px',
      fontFamily: 'Times-Roman',
    },
    area: {
      fontFamily: 'Helvetica', // 'Arial',
      fontSize: 14,
      lineHeight: 1.21,
    },
    areaEmphasis: {
      fontFamily: 'Times-Bold',
      fontSize: 16,
      lineHeight: 'inherit',

    },
    areaView: {
      position: 'absolute',
      top: '132px',
      left: '36px',
      width: '153px',
    },
    calloutView: {
      width: '92px',
      height: '92px',
      transform: 'rotate(-5deg)',
      textAlign: 'center',
      top: '220px',
      left: '58px',
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    calloutTitle: {
      width: '100%',
      fontSize: '14pt',
      fontFamily: 'Helvetica',
    },
    calloutZipCode: {
      width: '100%',
      fontSize: 30,
      lineHeight: 1.07,
      fontFamily: 'Times-Bold',
    },
    calloutBgView: {
      width: 151.5,
      height: 150,
      position: 'absolute',
      left: 24.5,
      top: 190,
      transform: 'rotate(-5deg)',
    },
    materialId: {
      bottom: 18,
      left: 36,
      width: 342,
      fontFamily: 'Courier',
      fontSize: 7,
      position: 'absolute',
      opacity: 0.5,
    },
    photo: {
      width: '220.5px',
      height: '220.5px',
      left: '158px',
      top: '96px',
      position: 'absolute',
      borderRadius: '110px',
      overflow: 'hidden',
    },
    portrait: {
      width: '265px',
      height: '267px',
      position: 'absolute',
      top: '308px',
      left: '36px',
      overflow: 'hidden',
      borderRadius: '133.5px',
    },
    portraitBg: {
      width: '265px',
      height: '267px',
      position: 'absolute',
      top: '308px',
      left: '36px',
      overflow: 'hidden',
      borderRadius: '133.5px',
      backgroundColor: primaryBrand.colors?.primary || '#F0F',
    },
    primaryLogo: primaryLogoDimensions,
    primaryLogoView: primaryLogoView,
    qr: {
      width: '95px',
      height: '95px',
      transform: 'rotate(5 deg)',
      top: '396px',
      left: '284px',
      position: 'absolute',
      backgroundColor: 'white',
    },
    qrBg: {
      width: '137px',
      height: '147px',
      top: '371px',
      left: '266px',
      position: 'absolute',
      transform: 'rotate(5 deg)',
    },
    textView: {
      width: '342px',
      height: '384px',
      top: '132px',
      left: '414px',
      display: 'flex',
      alignContent: 'center',
      justifyContent: 'center',
    },
    textHeader: {
      fontFamily: 'Times-Bold',
      fontSize: 28,
      lineHeight: 1.07,
      marginBottom: '14pt',
    },
    textBody: {
      fontFamily: 'Times-Roman',
      fontSize: 24,
      lineHeight: 1.17,
      marginBottom: '14pt',
    },
    textBodyEmphasis: {
      fontFamily: 'Times-Italic',
    },
    textCta: {
      fontFamily: 'Helvetica',
      fontSize: 14,
      lineHeight: 1.14,
    },
    textCtaLink: {
      color: primaryBrand.colors.dark,
      textDecoration: 'underline',
    },
  });

  // Debug
  // _.each(Styles, (style) => {
  //   style['border'] = '1px solid magenta';
  // });

  return (
    primaryLogoDimensions &&
      <Page
        size="LETTER"
        orientation="landscape">
        <View style={Styles.announcementView}>
          <Text style={Styles.announcement}>An Announcement About</Text>
          <Text style={Styles.announcementTheme}>{themeData.public_name}</Text>
        </View>
        <View style={Styles.primaryLogoView}>
          <PdfImageFromAzureWithUri
            fileUri={primaryBrand.logoUrl} 
            style={ Styles.primaryLogo }
            />
        </View>
        {
          isCoBranded && coBrand &&
            <>
              <View style={coBrandLogoView}>
                <PdfImageFromAzureWithUri
                  fileUri={formatImageSource(coBrand.logoUrl)}
                  style={{
                    ...coBrandLogoDimensions,
                    position: 'absolute',
                    bottom: 0,
                  }} />
              </View>
              <View style={lbLogoView}>
                <PdfImageFromAzureWithUri
                  fileUri={formatImageSource(lbBrand.logoUrl)}
                  style={{
                    ...lbLogoDimensions,
                    position: 'absolute',
                    bottom: 0,
                  }} />
              </View>
            </>
        }
        <View style={Styles.areaView}>
          <Text style={Styles.area}>
            To anyone living&#10;
            within <Text style={Styles.areaEmphasis}>{radiusToText(radius)}</Text>&#10;
            of ZIP code <Text style={Styles.areaEmphasis}>{zipCode}</Text>
          </Text>
        </View>
        <View style={Styles.photo}>
          <PdfImageFromAzureWithOptions
            style={{
              width: '100%',
              height: '100%',
            }}
            fileName={themeData.image_src}
            folderPath="assets/theme_images/"
            />
        </View>
        <View style={Styles.calloutBgView}>
          <ZipCodeSvg />
        </View>
        <View style={Styles.calloutView}>
          <Text style={Styles.calloutTitle}>
            ZIP Code
          </Text>
          <Text style={Styles.calloutZipCode}>
            {zipCode}
          </Text>
        </View>
        <View style={Styles.portraitBg}></View>
        <View style={Styles.portrait}>
          <PdfImageFromAzureWithOptions
            fileName={imgSrc}
            folderPath="assets/people_portraits/"
          />
        </View>
        <View style={Styles.materialId}>
          <Text>
            {/* {materialId()} */}
            {uuid}
          </Text>
        </View>
        <View style={Styles.qrBg}>
          <QrCodeSvg color={primaryBrand.colors?.primary} />
        </View>
        <View style={Styles.qr}>
          <PdfImageFromAzureWithOptions
            fileName={themeData.qr_code_src}
            folderPath="assets/theme_qrs/"
          />
        </View>
        <View style={Styles.textView}>
          <Text style={Styles.textHeader}>
            {themeData.header}
          </Text>
          <Text style={Styles.textBody}>
            <Text style={Styles.textBodyEmphasis}>
              {themeData.body_emphasis}
            </Text>
              &nbsp;{themeData.body}
          </Text>
          <Text style={Styles.textCta}>
            {themeData.cta}&nbsp;Scan the QR or visit <Link src={`https://${themeData.cta_link}`}><Text>{themeData.cta_link}</Text></Link>.
          </Text>
        </View>
      </Page>
  );
};

export default Flyer;
